import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Zap, AlertTriangle, CheckCircle, X, Sparkles, ChevronRight, TrendingUp, Activity, CreditCard, ExternalLink, Loader2 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useRealtimeEvents } from "../hooks/useRealtimeEvents";

export function AiCreditsIndicator() {
  const { t, fmt } = useLanguage();
  const [status, setStatus] = useState(null);
  const [usage, setUsage] = useState(null);
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [upgrading, setUpgrading] = useState("");
  const [tab, setTab] = useState("status");
  const [lastSpendAt, setLastSpendAt] = useState(null); // V3.4.1 — "🟢 just spent X" tooltip
  const [lastSpend, setLastSpend] = useState(null); // V3.4.1 — {operation, model, cost_usd}
  const ref = useRef(null);

  const fetchStatus = () => {
    api.get("/ai-credits/status").then(r => setStatus(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 120000);
    return () => clearInterval(interval);
  }, []);

  // V3.4.1 — Realtime SSE: refresh the pill instantly on any LLM spend
  // anywhere in the app. Replaces the 120s poll as the primary source
  // of freshness (poll stays as the safety net).
  // V3.4.5 — Cache the last N events in a ref so the hover tooltip can
  // show a rolling 30s "where is my money going" rollup.
  const spendHistoryRef = useRef([]); // [{ts, operation, model, cost_usd, credits_spent}, ...]
  const [historyVersion, setHistoryVersion] = useState(0); // bumps to force re-render
  // V3.4.8 — Surge alarm. `surgeStartRef` holds the timestamp of the
  // first event where projected $/min crossed $5; `alarmFiredRef`
  // ensures we only fire ONE toast per surge cycle (until the rate
  // drops back below $5 it stays armed-but-quiet).
  const surgeStartRef = useRef(null);
  const alarmFiredRef = useRef(false);
  useRealtimeEvents(useCallback((evt) => {
    if (evt?.type !== "emergent_credits_changed") return;
    fetchStatus();
    setLastSpendAt(Date.now());
    setLastSpend({
      operation: evt.operation,
      model: evt.model,
      cost_usd: evt.cost_usd,
      credits_spent: evt.credits_spent,
    });
    // V3.4.5 — Append to the rolling history (cap at 20 entries).
    const now = Date.now();
    const hist = spendHistoryRef.current;
    hist.push({
      ts: now,
      operation: evt.operation || "unknown",
      model: evt.model,
      cost_usd: Number(evt.cost_usd) || 0,
      credits_spent: Number(evt.credits_spent) || 0,
    });
    // Drop entries older than 30s OR keep only the last 20
    const cutoff = now - 30_000;
    spendHistoryRef.current = hist.filter((e) => e.ts >= cutoff).slice(-20);
    setHistoryVersion((v) => v + 1);

    // V3.4.8 — Surge detection. Computed inline so we get the freshest
    // rate the moment the event lands. Threshold: $5/min sustained for
    // 60 seconds → ONE toast per surge cycle.
    const currentRate = spendHistoryRef.current
      .reduce((sum, e) => sum + (e.cost_usd || 0), 0) * 2; // 30s × 2 = per-minute
    if (currentRate > 5) {
      if (surgeStartRef.current == null) {
        surgeStartRef.current = now;
      } else if (!alarmFiredRef.current && (now - surgeStartRef.current) >= 60_000) {
        const topOp = (() => {
          const tally = {};
          for (const x of spendHistoryRef.current) {
            const k = x.operation || x.model || "unknown";
            tally[k] = (tally[k] || 0) + (x.cost_usd || 0);
          }
          return Object.entries(tally).sort(([, a], [, b]) => b - a)[0]?.[0] || "unknown";
        })();
        toast.error("🔥 AI spend surging", {
          description: `USD $${currentRate.toFixed(4)}/min projected · last 60s · top operation: ${topOp}`,
          duration: 8000,
        });
        alarmFiredRef.current = true;
      }
    } else {
      // Rate dropped below the threshold — disarm so the next surge
      // cycle can fire fresh.
      surgeStartRef.current = null;
      alarmFiredRef.current = false;
    }
  }, []));

  // V3.4.1 — Auto-clear the "🟢 just spent" tooltip after 5s.
  useEffect(() => {
    if (!lastSpendAt) return undefined;
    const t = setTimeout(() => { setLastSpendAt(null); setLastSpend(null); }, 5000);
    return () => clearTimeout(t);
  }, [lastSpendAt]);

  // V3.4.5 — Periodically prune entries older than 30s so the tooltip
  // shrinks on its own once spend dries up (we don't need a re-render
  // for the OFFSET — useState `historyVersion` is bumped on every
  // event AND once per 5s by this interval to keep the UI honest).
  useEffect(() => {
    const t = setInterval(() => {
      const cutoff = Date.now() - 30_000;
      const before = spendHistoryRef.current.length;
      spendHistoryRef.current = spendHistoryRef.current.filter((e) => e.ts >= cutoff);
      if (spendHistoryRef.current.length !== before) {
        setHistoryVersion((v) => v + 1);
      }
    }, 5000);
    return () => clearInterval(t);
  }, []);

  // V3.4.5 — Build the rollup that the tooltip renders.
  const spendRollup = (() => {
    void historyVersion; // re-runs whenever the version bumps
    const cutoff = Date.now() - 30_000;
    const recent = spendHistoryRef.current.filter((e) => e.ts >= cutoff);
    if (recent.length === 0) return null;
    const byOp = {};
    let totalUsd = 0;
    for (const e of recent) {
      const key = e.operation || e.model || "unknown";
      byOp[key] = byOp[key] || { count: 0, cost_usd: 0 };
      byOp[key].count += 1;
      byOp[key].cost_usd += e.cost_usd || 0;
      totalUsd += e.cost_usd || 0;
    }
    const ops = Object.entries(byOp)
      .sort(([, a], [, b]) => b.cost_usd - a.cost_usd)
      .slice(0, 5);
    // V3.4.7 — Projected burn rate. The 30s window normalizes to per
    // minute. If spend is concentrated in the last few events the
    // projection naturally overshoots — that's the alarm we want.
    const burn_rate_per_min = totalUsd * 2; // 30s × 2 = 1 min
    return { count: recent.length, total_usd: totalUsd, ops, burn_rate_per_min };
  })();

  useEffect(() => {
    if (open && tab === "usage" && !usage) {
      api.get("/ai-credits/usage?days=30").then(r => setUsage(r.data)).catch(() => {});
    }
  }, [open, tab, usage]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (status?.status === "depleted") {
      toast.error(t("AI credits depleted — image generation and AI features are paused."), {
        id: "ai-credits-depleted",
        duration: 10000,
        action: { label: "View", onClick: () => setOpen(true) },
      });
    }
  }, [status?.status, t]);

  const handleTestKey = async () => {
    setTesting(true);
    try {
      const res = await api.post("/ai-credits/test-key");
      if (res.data.working) {
        toast.success(t("AI key is working!"));
        fetchStatus();
      } else {
        toast.error(res.data.error || t("Key test failed"));
      }
    } catch {
      toast.error(t("Failed to test key"));
    } finally {
      setTesting(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgrading(planId);
    try {
      const res = await api.post("/billing/checkout", {
        plan: planId,
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.open(res.data.url, "_blank");
        toast.success(t("Checkout opened in new tab"));
      }
    } catch (e) {
      const msg = e.response?.data?.detail || t("Failed to create checkout");
      toast.error(msg);
    } finally {
      setUpgrading("");
    }
  };

  if (!status) return null;

  const isDepleted = status.status === "depleted" || status.alert_level === "depleted";
  const noKey = status.status === "no_key";
  const isActive = status.status === "active";
  const alertLevel = status.alert_level || "normal";
  const usagePct = status.usage_percentage || 0;
  const currentPlan = status.plan || "free";

  // Sprint 1066b — flashpill bug fix: cap insane percentages (>100% renders
  // as "44188%" which is meaningless). Also derive a friendly $ spent label
  // for the depleted state per user choice Q4=b.
  const monthlyUsed = status.monthly_credits_used || 0;
  const creditsToUsd = (c) => (c / 1000).toFixed(2);  // 1000 credits ≈ $1 internal accounting
  const cappedPctLabel = usagePct > 100 ? "100%+" : `${Math.round(usagePct)}%`;
  const pillLabel = isDepleted
    ? `Depleted · USD $${creditsToUsd(monthlyUsed)}`
    : noKey
      ? "No Key"
      : cappedPctLabel;

  const alertColors = {
    normal: "text-emerald-400", warning: "text-amber-400",
    critical: "text-orange-400", depleted: "text-red-400",
  };
  const alertBg = {
    normal: "bg-emerald-500/10 border-emerald-500/20", warning: "bg-amber-500/10 border-amber-500/20",
    critical: "bg-orange-500/10 border-orange-500/20", depleted: "bg-red-500/10 border-red-500/20",
  };
  const progressColor = alertLevel === "normal" ? "#10b981" : alertLevel === "warning" ? "#f59e0b" : alertLevel === "critical" ? "#f97316" : "#ef4444";

  const tierColors = { starter: "#3B82F6", pro: "#8B5CF6", enterprise: "#F59E0B" };

  return (
    <div className="relative group" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl transition-all text-xs font-medium ${
          isDepleted ? "bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20"
          : alertLevel === "critical" ? "bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 border border-orange-500/20"
          : alertLevel === "warning" ? "bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20"
          : noKey ? "bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 border border-yellow-500/20"
          : "hover:bg-[var(--app-surface)] text-[var(--app-text-muted)] border border-transparent"
        }`} data-testid="ai-credits-indicator"
        title={isDepleted ? `Used ${monthlyUsed.toLocaleString()} credits this month. Upgrade your plan or add balance.` : (lastSpend ? `🟢 just spent ${lastSpend.cost_usd ? 'USD $' + Number(lastSpend.cost_usd).toFixed(4) : (lastSpend.credits_spent || 0) + ' credits'} on ${lastSpend.operation || lastSpend.model || 'AI'}` : `${Math.round(usagePct)}% of monthly credits used`)}>
        <Zap className={`w-3.5 h-3.5 ${alertColors[isDepleted ? "depleted" : alertLevel] || alertColors.normal}`} />
        <span className="hidden sm:inline tabular-nums" data-testid="ai-credits-pill-label">
          {pillLabel}
        </span>
        {(isDepleted || alertLevel === "critical") && (
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        )}
        {/* V3.4.1 — "🟢 just spent" emerald glow ring after a fresh SSE spend event. */}
        {lastSpendAt && (
          <span
            className="absolute -inset-0.5 rounded-xl border border-emerald-400/60 animate-pulse pointer-events-none"
            data-testid="ai-credits-just-spent"
          />
        )}
      </button>

      {/* V3.4.5 — Rolling 30s spend rollup tooltip. Hover-only, hidden
          when the click-open dropdown is showing. Driven by the
          `spendHistoryRef` (mutated by every `emergent_credits_changed`
          event) so the math is always fresh on hover. */}
      {!open && spendRollup && (
        <div
          className="absolute right-0 top-full mt-1.5 w-72 rounded-lg border border-emerald-500/30 bg-[var(--app-bg)] shadow-xl z-40 overflow-hidden opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150"
          data-testid="ai-credits-spend-rollup"
        >
          <div className="px-3 py-2 border-b border-[var(--app-border)] flex items-center justify-between">
            <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-300">
              last 30s · {spendRollup.count} {spendRollup.count === 1 ? "event" : "events"}
            </div>
            <div className="text-[11px] font-mono tabular-nums text-[var(--app-text)]">
              USD ${spendRollup.total_usd.toFixed(4)}
            </div>
          </div>
          <div className="px-3 py-2 space-y-1">
            {spendRollup.ops.map(([op, agg]) => (
              <div
                key={op}
                className="flex items-center justify-between gap-2 text-[11px] font-mono"
                data-testid={`ai-credits-spend-row-${op}`}
              >
                <span className="text-[var(--app-text)] truncate">
                  {agg.count}× <span className="text-[var(--app-text-muted)]">{op}</span>
                </span>
                <span className="text-emerald-300 tabular-nums shrink-0">
                  USD ${agg.cost_usd.toFixed(4)}
                </span>
              </div>
            ))}
          </div>
          <div className="px-3 py-1.5 border-t border-[var(--app-border)] space-y-1">
            {/* V3.4.7 — Projected $/min burn rate. Amber over $1/min, rose over $5/min. */}
            {(() => {
              const rate = spendRollup.burn_rate_per_min;
              const tone = rate > 5
                ? { bar: "bg-rose-400", text: "text-rose-300", label: "🔥 fast burn" }
                : rate > 1
                  ? { bar: "bg-amber-400", text: "text-amber-300", label: "⚡ active burn" }
                  : { bar: "bg-emerald-400", text: "text-emerald-300", label: "🟢 steady" };
              // Clamp visual bar to 100% at $10/min (above that we're already on fire).
              const pct = Math.min(100, (rate / 10) * 100);
              return (
                <>
                  <div className="flex items-center justify-between text-[10px] font-mono">
                    <span className={tone.text}>burn rate · {tone.label}</span>
                    <span className="tabular-nums text-[var(--app-text)]" data-testid="ai-credits-burn-rate">
                      USD ${rate.toFixed(4)}/min projected
                    </span>
                  </div>
                  <div className="h-1 rounded-full bg-zinc-800 overflow-hidden">
                    <div
                      className={`h-full ${tone.bar} transition-all duration-300 ease-out`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </>
              );
            })()}
            <div className="text-[9px] font-mono text-[var(--app-text-muted)] text-center pt-0.5">
              live · auto-prunes after 30s of silence
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[420px] rounded-2xl border border-[var(--app-border)] shadow-2xl z-50 overflow-hidden"
          style={{ backgroundColor: "var(--app-bg)" }} data-testid="ai-credits-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--app-border)]">
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${alertBg[alertLevel]}`}>
                {isDepleted ? <AlertTriangle className="w-4 h-4 text-red-400" /> : isActive ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-yellow-400" />}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[var(--app-text)]">{t("AI Credits")}</h3>
                <p className="text-[10px] text-[var(--app-text-muted)]">
                  {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} Plan
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded-lg hover:bg-[var(--app-surface)]" data-testid="ai-credits-close">
              <X className="w-4 h-4 text-[var(--app-text-muted)]" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-[var(--app-border)]">
            {[
              { id: "status", label: "Status", icon: Zap },
              { id: "usage", label: "Usage", icon: TrendingUp },
              { id: "plans", label: "Plans", icon: CreditCard },
            ].map(t2 => (
              <button key={t2.id} onClick={() => setTab(t2.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                  tab === t2.id ? "text-[var(--app-blue)] border-b-2 border-[var(--app-blue)]" : "text-[var(--app-text-muted)] hover:text-[var(--app-text)]"
                }`} data-testid={`ai-credits-tab-${t2.id}`}>
                <t2.icon className="w-3.5 h-3.5" />{t2.label}
              </button>
            ))}
          </div>

          {/* Status Tab */}
          {tab === "status" && (
            <div className="p-4 space-y-4">
              <div className="space-y-2" data-testid="ai-credits-meter">
                <div className="flex justify-between items-end">
                  <span className="text-xs text-[var(--app-text-muted)]">Monthly Usage</span>
                  <span className={`text-sm font-bold ${alertColors[alertLevel]}`}>
                    {fmt.num(status.monthly_credits_used || 0, 0)} / {fmt.num(status.monthly_limit || 0, 0)}
                  </span>
                </div>
                <div className="h-3 rounded-full bg-[var(--app-surface)] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${Math.min(usagePct, 100)}%`, backgroundColor: progressColor }}
                    data-testid="ai-credits-progress" />
                </div>
                <p className="text-[10px] text-[var(--app-text-muted)]">
                  {fmt.num(Math.max(0, (status.monthly_limit || 0) - (status.monthly_credits_used || 0)), 0)} credits remaining
                </p>
              </div>

              {(isDepleted || alertLevel === "critical") && (
                <div className={`p-3 rounded-xl border ${alertBg[isDepleted ? "depleted" : alertLevel]}`} data-testid="ai-credits-alert-banner">
                  <p className={`text-xs font-semibold ${alertColors[isDepleted ? "depleted" : alertLevel]}`}>
                    {isDepleted ? t("AI Budget Exhausted") : t("Approaching Limit")}
                  </p>
                  <p className={`text-[10px] mt-0.5 ${alertColors[isDepleted ? "depleted" : alertLevel]} opacity-70`}>
                    {isDepleted ? "AI features are paused. Upgrade your plan or add balance." : `You've used ${Math.round(usagePct)}% of your monthly credits.`}
                  </p>
                  {isDepleted && (
                    <button onClick={() => setTab("plans")} className="mt-2 text-[10px] font-medium text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      View Plans <ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <button onClick={handleTestKey} disabled={testing}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[var(--app-surface)] transition-colors text-left group"
                  data-testid="ai-credits-test-key">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[var(--app-text)]">{testing ? "Testing..." : "Test AI Connection"}</p>
                    <p className="text-[10px] text-[var(--app-text-muted)]">{t("Verify your key works and has budget")}</p>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-[var(--app-text-muted)] opacity-0 group-hover:opacity-100" />
                </button>
              </div>

              {status.credit_costs && (
                <div className="pt-2 border-t border-[var(--app-border)]">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-[var(--app-text-muted)] mb-2">{t("Credit Costs")}</p>
                  <div className="grid grid-cols-2 gap-1">
                    {Object.entries(status.credit_costs).slice(0, 8).map(([op, cost]) => (
                      <div key={op} className="flex justify-between items-center px-2 py-1 rounded-lg bg-[var(--app-surface)]">
                        <span className="text-[10px] text-[var(--app-text-muted)] truncate">{op.replaceAll("_", " ")}</span>
                        <span className="text-[10px] font-bold text-[var(--app-text)] ml-1">{cost}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Usage Tab */}
          {tab === "usage" && (
            <div className="p-4 space-y-4" data-testid="ai-credits-usage-tab">
              {!usage ? (
                <div className="flex items-center justify-center py-8">
                  <Activity className="w-5 h-5 text-[var(--app-text-muted)] animate-pulse" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 rounded-xl bg-[var(--app-surface)]">
                      <p className="text-[10px] text-[var(--app-text-muted)]">Total Credits</p>
                      <p className="text-lg font-bold text-[var(--app-text)]">{fmt.num(usage.total_credits, 0)}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-[var(--app-surface)]">
                      <p className="text-[10px] text-[var(--app-text-muted)]">Total Requests</p>
                      <p className="text-lg font-bold text-[var(--app-text)]">{fmt.num(usage.total_requests, 0)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-[var(--app-text-muted)] mb-2">By Operation (30d)</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {(usage.operations || []).map(op => {
                        const pct = usage.total_credits > 0 ? (op.total_credits / usage.total_credits) * 100 : 0;
                        return (
                          <div key={op.operation} className="flex items-center gap-2" data-testid={`ai-usage-${op.operation}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-center mb-0.5">
                                <span className="text-[10px] text-[var(--app-text)] truncate">{(op.operation || "").replaceAll("_", " ")}</span>
                                <span className="text-[10px] font-medium text-[var(--app-text-muted)]">{op.total_credits} credits</span>
                              </div>
                              <div className="h-1.5 rounded-full bg-[var(--app-surface)] overflow-hidden">
                                <div className="h-full rounded-full bg-[var(--app-blue)]" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                            <span className="text-[9px] text-[var(--app-text-muted)] w-8 text-right">{op.count}x</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {(usage.daily_trend || []).length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-[var(--app-text-muted)] mb-2">Daily Trend</p>
                      <div className="h-16 flex items-end gap-px">
                        {(usage.daily_trend || []).slice(-14).map((d, i) => {
                          const max = Math.max(...(usage.daily_trend || []).slice(-14).map(x => x.credits));
                          const h = max > 0 ? (d.credits / max) * 100 : 0;
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center" title={`${d.date}: ${d.credits} credits`}>
                              <div className="w-full rounded-t bg-[var(--app-blue)] opacity-70" style={{ height: `${Math.max(h, 4)}%` }} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Plans Tab */}
          {tab === "plans" && (
            <div className="p-4 space-y-3" data-testid="ai-credits-plans-tab">
              <p className="text-xs text-[var(--app-text-muted)]">
                {t("Upgrade your plan to get more AI credits and features")}
              </p>
              {(status.tiers || []).map(tier => {
                const isCurrent = currentPlan === tier.id;
                const isDowngrade = (currentPlan === "enterprise" && tier.id !== "enterprise") || (currentPlan === "pro" && tier.id === "starter");
                const color = tierColors[tier.id] || "var(--app-text-muted)";
                return (
                  <div key={tier.id}
                    className={`p-3 rounded-xl border transition-all ${isCurrent ? "border-[var(--app-blue)]/50 bg-[var(--app-blue)]/5" : "border-[var(--app-border)] hover:border-[var(--app-border-hover)]"}`}
                    data-testid={`plan-${tier.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                        <span className="text-sm font-semibold text-[var(--app-text)]">{tier.name}</span>
                        {isCurrent && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--app-blue)]/20 text-[var(--app-blue)] font-medium">Current</span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-[var(--app-text)]">USD ${tier.price}</span>
                        <span className="text-[10px] text-[var(--app-text-muted)]">/mo</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2.5">
                      {(tier.features || []).slice(0, 4).map((f, i) => (
                        <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-md bg-[var(--app-surface)] text-[var(--app-text-muted)]">{f}</span>
                      ))}
                    </div>
                    {!isCurrent && !isDowngrade && (
                      <button onClick={() => handleUpgrade(tier.id === "starter" ? "pro" : tier.id)}
                        disabled={!!upgrading}
                        className="w-full py-2 rounded-lg text-xs font-medium text-white transition-all flex items-center justify-center gap-1.5 hover:opacity-90 disabled:opacity-50"
                        style={{ backgroundColor: color }}
                        data-testid={`upgrade-${tier.id}`}>
                        {upgrading === tier.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
                        {upgrading === tier.id ? t("Opening checkout...") : t("Upgrade")}
                      </button>
                    )}
                    {isCurrent && (
                      <div className="text-center py-1.5 text-[10px] text-[var(--app-text-muted)]">{t("Your current plan")}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-[var(--app-border)] px-4 py-2.5 flex items-center justify-between">
            <p className="text-[10px] text-[var(--app-text-muted)]">
              <strong>Profile &rarr; Universal Key</strong> {t("to add Emergent balance")}
            </p>
            <button onClick={() => { setTab("plans"); }} className="text-[10px] text-[var(--app-blue)] hover:underline font-medium" data-testid="view-plans-link">
              {t("View Plans")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
